/**
 * Claims API Client
 *
 * Provides methods for fetching and submitting insurance claims.
 * All fetch methods unwrap the standard API envelope
 * { success, message, timestamp, data: T } and return T directly.
 */

// ─── Response types (unwrapped — match the `data` field of the API envelope) ─

export interface ClaimItem {
  claim_id: string;
  claim_number: string;
  policy_id: string;
  claim_type: string;
  claim_amount: number;
  approved_amount?: number | null;
  claim_status: string;
  incident_date: string;
  submitted_date: string;
  decision_date?: string | null;
  settlement_date?: string | null;
  adjuster_notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClaimsResponse {
  claims: ClaimItem[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

export interface ClaimDetailsResponse extends ClaimItem {
  policy: {
    policy_id: string;
    policy_number: string;
    coverage_amount: number;
    premium_amount: number;
    policy_status: string;
  };
  supporting_documents: Array<{
    document_id: string;
    document_type: string;
    file_name: string;
    uploaded_at: string;
  }>;
  processing_history: Array<{
    event_type: string;
    timestamp: string;
    notes: string;
  }>;
}

export interface SubmitClaimRequest {
  claim_type: string;
  claim_amount: number;
  incident_date: string;
  description?: string;
  policy_id?: string;
}

export type SubmitClaimResponse = ClaimItem;

// ─── Fetch infrastructure ─────────────────────────────────────────────────────

export type ClaimsFetchFunction = (
  url: string,
  options?: RequestInit,
) => Promise<Response>;

function defaultFetch(url: string, options?: RequestInit): Promise<Response> {
  return fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
}

let _fetch: typeof fetch = fetch;

export function configureClaimsApi(fetchFn: typeof fetch): void {
  _fetch = fetchFn;
}

async function unwrap<T>(response: Response): Promise<T> {
  const body = await response.json();
  if (!response.ok) {
    throw new Error(
      body?.error?.message || `HTTP ${response.status}: ${response.statusText}`,
    );
  }
  return body.data as T;
}

// ─── API client ───────────────────────────────────────────────────────────────

export const claimsApi = {
  async fetchAll(
    baseUrl = "/api/v1",
    params?: { limit?: number; offset?: number; status?: string },
  ): Promise<ClaimsResponse> {
    const query = new URLSearchParams();
    if (params?.limit) query.set("limit", String(params.limit));
    if (params?.offset) query.set("offset", String(params.offset));
    if (params?.status) query.set("status", params.status);
    const qs = query.toString();
    const res = await _fetch(`${baseUrl}/claims${qs ? `?${qs}` : ""}`);
    return unwrap<ClaimsResponse>(res);
  },

  async fetchById(
    claimId: string,
    baseUrl = "/api/v1",
  ): Promise<ClaimDetailsResponse> {
    const res = await _fetch(`${baseUrl}/claims/${claimId}`);
    return unwrap<ClaimDetailsResponse>(res);
  },

  async submitClaim(
    claim: SubmitClaimRequest & { policy_id?: string },
    baseUrl = "/api/v1",
  ): Promise<SubmitClaimResponse> {
    const res = await _fetch(`${baseUrl}/claims`, {
      method: "POST",
      body: JSON.stringify(claim),
    });
    return unwrap<SubmitClaimResponse>(res);
  },
};
